import { useForm } from "react-hook-form";
import {
  AliasProductFormInput,
  aliasProductSchema,
} from "../schemas/aliasProductSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { AliasProduct } from "../types/AliasProduct";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  VStack,
} from "@chakra-ui/react";
import { ModalSearchProduct } from "@/core/products/components/ModalSearchProduct";
import { useEffect, useState } from "react";
import { ProductState } from "@/core/products/components/ProductsTable";
import CardProductDetails from "@/core/products/components/CardProductDetails";
import { getProductByCode } from "@/core/products/api/getProduct";
import { LuSearch } from "react-icons/lu";
import { Field } from "@/ui/chakra/field";
import { toast } from "@/ui/chakra/toaster";

interface AliasProductFormProps {
  onSubmit: (data: AliasProductFormInput) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  selectedAliasProduct?: AliasProduct;
}

const AliasProductForm: React.FC<AliasProductFormProps> = ({
  onSubmit,
  onCancel,
  selectedAliasProduct,
  isSubmitting = false,
}) => {
  const [isOpenModalSearch, setIsOpenModalSearch] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductState | null>(
    selectedAliasProduct
      ? {
          data: selectedAliasProduct.product,
          amount: 1,
        }
      : null,
  );

  const [searchCode, setSearchCode] = useState<string>("");
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    setError,
  } = useForm<AliasProductFormInput>({
    resolver: zodResolver(aliasProductSchema),
    defaultValues: {
      description: selectedAliasProduct?.description || "",
      name: selectedAliasProduct?.name || "",
      productId: selectedAliasProduct?.productId || "",
    },
  });

  useEffect(() => {
    if (selectedProduct?.data.id) {
      setValue("productId", selectedProduct.data.id);
      setError("productId", {
        message: "",
      });
    }
  }, [setValue, selectedProduct, setError]);

  const handleSearchByCode = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const product = await getProductByCode(searchCode);
      if (product) {
        setSelectedProduct({
          data: product,
          amount: 1,
        });
        return;
      }
      toast({
        title: "Error",
        description: "Producto no encontrado",
        status: "error",
        duration: 5000,
      });
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack gap={4} align="stretch">
        <Field
          label="Nombre"
          required
          invalid={errors.name !== undefined}
          errorText={errors.name?.message}
        >
          <Input {...register("name")} placeholder="Nombre" />
        </Field>
        <Field label="Descripción">
          <Input {...register("description")} placeholder="Descripción" />
        </Field>
        <Field
          label="Buscar por código"
          required
          invalid={!!errors.productId?.message}
          errorText={errors.productId?.message}
          helperText="Escribe el código y presiona Enter, o abre el buscador."
        >
          <HStack w="full">
            <Input
              placeholder="Ingrese el codigo a buscar"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value)}
              onKeyDown={handleSearchByCode}
            />
            <IconButton
              aria-label="Abrir buscador de productos"
              colorPalette="brand"
              onClick={() => setIsOpenModalSearch(true)}
              type="button"
            >
              <LuSearch />
            </IconButton>
          </HStack>
        </Field>

        {selectedProduct && (
          <Box width={"100%"}>
            <CardProductDetails
              product={selectedProduct.data}
              title="Producto seleccionado"
            />
          </Box>
        )}
        <HStack
          width="100%"
          justifyContent="flex-end"
          position="sticky"
          bottom={0}
          mt={2}
          mx={-6}
          px={6}
          py={3}
          bg="bg.panel"
          borderTopWidth="1px"
          borderColor="border"
          zIndex={1}
        >
          <Button variant="outline" onClick={onCancel} type="button">
            Cancelar
          </Button>
          <Button colorPalette="brand" type="submit" loading={isSubmitting}>
            Guardar
          </Button>
        </HStack>
      </VStack>
      <ModalSearchProduct
        isOpen={isOpenModalSearch}
        onClose={() => setIsOpenModalSearch(false)}
        onChange={(product) => {
          setValue("productId", product.data.id);
          setSelectedProduct(product);
          setIsOpenModalSearch(false);
        }}
        selected={selectedProduct ? [selectedProduct] : []}
      />
    </form>
  );
};

export default AliasProductForm;
